// src/pages/PricingPage.tsx
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, CheckCircle, Zap } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'

interface Plan {
  id: string
  title: string
  price: number
  originalPrice: number
  period: 'monthly' | 'yearly'
  priceId: string
  features: string[]
  isRecommended?: boolean
  savings: string
  badge?: string
}

const pricingPlans: Plan[] = [
  {
    id: 'monthly',
    title: 'Monthly Plan',
    price: 14.99,
    originalPrice: 14.99,
    period: 'monthly',
    priceId:
      import.meta.env.VITE_STRIPE_PRICE_MONTHLY_ID ||
      'price_1RUm3EDsuZmPhpuHyZKTq5BF',
    savings: 'Pay as you go',
    features: [
      'Track your trading discipline',
      'Build green discipline momentum',
      'Easy, clean, intuitive interface',
      'Focus on essential metrics',
      'Start journaling in seconds',
      'Journal without pain',
      '1 AI Trading Coach analysis & up to 2 questions every 48 hours',
    ],
  },
  {
    id: 'yearly',
    title: 'Yearly Plan',
    price: 99.99,
    originalPrice: 179.88,
    period: 'yearly',
    priceId:
      import.meta.env.VITE_STRIPE_PRICE_YEARLY_ID ||
      'price_1RUm3nDsuZmPhpuHlKLSGKsD',
    isRecommended: true,
    badge: 'MOST POPULAR',
    savings: 'SAVE 44%',
    features: [
      'Everything in Monthly Plan',
      'Track your trading discipline',
      'Build green discipline momentum',
      'Easy, clean, intuitive interface',
      'Focus on essential metrics',
      'Start journaling in seconds',
      'Journal without pain',
      '1 AI Trading Coach analysis & up to 5 questions PER DAY',
      '🎯 Priority customer support',
      '💰 Lock in this price forever',
    ],
  },
]

export default function PricingPage() {
  const [emails, setEmails] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useState<'monthly' | 'yearly'>('yearly') // Default to yearly for anchoring
  const [couponCode, setCouponCode] = useState('')
  const [hasDiscount, setHasDiscount] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const coupon = params.get('coupon')
    if (coupon) {
      setCouponCode(coupon)
      setHasDiscount(true)
    }
  }, [])

  const handleEmailChange = (planId: string, value: string) =>
    setEmails(prev => ({ ...prev, [planId]: value }))

  const handleCheckout = async (planId: string) => {
    const email = emails[planId] || ''
    const priceId = pricingPlans.find(p => p.id === planId)!.priceId
    const referral = new URLSearchParams(window.location.search).get('ref') || ''

    setLoading(true)
    try {
      const res = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          email,
          referral,
          ...(couponCode ? { couponCode } : {}),
        }),
      })
      if (!res.ok) throw new Error(await res.text() || res.statusText)
      const { url } = await res.json()
      window.location.href = url
    } catch (err: any) {
      console.error('Checkout error:', err)
      alert('Failed to start checkout. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const currentPlan = pricingPlans.find(p => p.id === selectedTab)!
  const yearlyPlan = pricingPlans.find(p => p.id === 'yearly')!
  const email = emails[currentPlan.id] || ''

  return (
    <div data-page="pricing" className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="container mx-auto max-w-2xl mb-6 flex-grow">
        <div className="mb-4">
          <Button
            as={Link}
            to="/"
            variant="ghost"
            className="flex items-center"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back to Home
          </Button>
        </div>

        <Card>
          <CardContent className="px-6 md:px-8 py-6">

          {/* Promotional Banner */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-2 animate-pulse">
              🔥 LIMITED TIME: SAVE 44% 🔥
            </div>
          </div>

          {/* Hero Text */}
          <div className="text-center mb-4">
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-2 text-gray-950">
              Choose Your <span className="gradient-text">Success Plan</span>
            </h1>
            <p className="text-gray-600 text-lg">Join 100+ disciplined traders this month</p>
          </div>

          {/* Plan Period Toggle - Only 2 options */}
          <div className="max-w-2xl mx-auto mb-4">
            <div className="bg-gray-200/80 backdrop-blur-sm rounded-xl p-1 shadow-md inline-flex w-full">
              <button
                onClick={() => setSelectedTab('monthly')}
                className={`flex-1 px-4 md:px-5 py-3 rounded-lg font-medium transition-all duration-200 text-center ${
                  selectedTab === 'monthly'
                    ? 'bg-gray-100 border border-gray-300 shadow-md text-gray-700 font-semibold'
                    : 'text-gray-500 hover:bg-white/80 hover:text-gray-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedTab('yearly')}
                className={`flex-1 px-4 md:px-5 py-3 rounded-lg font-medium transition-all duration-200 text-center relative ${
                  selectedTab === 'yearly'
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 border border-emerald-400 shadow-lg text-white font-bold'
                    : 'text-gray-500 hover:bg-white/80 hover:text-gray-700'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <Zap size={16} />
                  Yearly
                </div>
                {yearlyPlan.isRecommended && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                    SAVE 44%
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Plan Details */}
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-2">
              <h3 className="text-xl font-display font-bold mb-1 text-gray-900">
                {currentPlan.title}
              </h3>
              {currentPlan.badge && (
                <div className="inline-flex items-center bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-3 py-1 rounded-full text-sm font-bold mb-3">
                  {currentPlan.badge}
                </div>
              )}
            </div>

            {/* Price Display with Strong Anchoring */}
            <div className="mb-4 relative text-center">
              <div className={`absolute -inset-4 rounded-lg blur-lg opacity-75 ${
                currentPlan.isRecommended 
                  ? 'bg-gradient-to-r from-emerald-100 to-emerald-50' 
                  : 'bg-gradient-to-r from-gray-100 to-gray-50'
              }`}></div>
              <div className="relative">
                {/* Show savings comparison for yearly */}
                {selectedTab === 'yearly' && (
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="text-center">
                      <div className="text-sm text-gray-500">Monthly would cost:</div>
                      <div className="text-xl font-bold text-gray-400 line-through">
                        ${currentPlan.originalPrice.toFixed(2)}/year
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Price Display */}
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className={`text-5xl font-display font-bold ${
                    currentPlan.isRecommended ? 'text-emerald-600' : 'text-gray-900'
                  }`}>
                    ${currentPlan.price.toFixed(2)}
                  </span>
                  <span className={`text-xl ${
                    currentPlan.isRecommended ? 'text-emerald-600' : 'text-gray-600'
                  }`}>
                    /{currentPlan.period}
                  </span>
                </div>

                {/* Savings Message */}
                <div className="text-center">
                  <p className={`text-lg font-bold ${
                    currentPlan.isRecommended ? 'text-emerald-600' : 'text-gray-600'
                  }`}>
                    {currentPlan.savings}
                  </p>
                  {selectedTab === 'yearly' && (
                    <p className="text-sm text-emerald-600 font-medium">
                      That's only $8.33/month - less than a coffee!
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Features List */}
            <div className="space-y-2 mb-6">
              {currentPlan.features.map((feature, idx) => (
                <div key={idx} className="flex items-start">
                  <CheckCircle
                    size={18}
                    className={`mr-3 mt-0.5 flex-shrink-0 ${
                      currentPlan.isRecommended ? 'text-emerald-500' : 'text-primary-500'
                    }`}
                  />
                  <p className={`text-gray-700 ${
                    feature.includes('🎯') || feature.includes('💰') || feature.includes('AI Trading Coach')
                      ? 'font-bold' 
                      : ''
                  }`}>
                    {feature}
                  </p>
                </div>
              ))}
            </div>

            {/* Urgency Message */}
            <div className={`border rounded-lg p-3 mb-4 ${
              currentPlan.isRecommended 
                ? 'bg-emerald-50 border-emerald-200' 
                : 'bg-orange-50 border-orange-200'
            }`}>
              <p className={`text-sm font-bold text-center ${
                currentPlan.isRecommended ? 'text-emerald-800' : 'text-orange-800'
              }`}>
                {selectedTab === 'yearly' 
                  ? '🎯 Most traders save $80/year with this plan' 
                  : '⏰ Upgrade to yearly anytime and save 44%'
                }
              </p>
            </div>

            {/* Checkout Form */}
            <form
              onSubmit={e => {
                e.preventDefault()
                handleCheckout(currentPlan.id)
              }}
              className="space-y-3"
            >
              <input
                type="email"
                value={email}
                onChange={e => handleEmailChange(currentPlan.id, e.target.value)}
                placeholder="Enter your email to get started"
                className="w-full p-4 border border-gray-300/50 rounded-lg bg-white/50 backdrop-blur-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-300 text-lg"
                required
              />
              <Button
                type="submit"
                disabled={!email || loading}
                className={`w-full !rounded-full py-5 text-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center ${
                  !email || loading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : currentPlan.isRecommended
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
                    : 'bg-black hover:bg-gray-900 text-white'
                }`}
              >
                {loading ? 'Processing...' : selectedTab === 'yearly' ? 'Lock In 44% Savings' : 'Start Monthly Plan'}
                <ArrowRight size={24} className="ml-3" />
              </Button>
            </form>

            {/* Risk Reversal */}
            <p className="text-center text-sm text-gray-500 mt-4">
              ✅ Cancel anytime • ✅ No setup fees • ✅ Instant access
            </p>
          </div>
          </CardContent>
        </Card>
      </div>

    </div>
  )
}